/** Completed worker results can each trigger expensive synchronous construction.
 * Spend a small budget each frame instead of a burst of promise callbacks. Cheap
 * patches can batch during loading; a costly scatter build gets its own frame.
 * A task itself is synchronous and cannot be preempted by this budget. */
export class FrameQueue<T> {
  private readonly ready: T[] = [];
  private scheduled: number | null = null;
  private closed = false;
  private draining = false;
  private workBudget?: { size: (value: T) => number; limit: number };

  constructor(private readonly consume: (value: T) => void,
    private readonly schedule: (callback: FrameRequestCallback) => number = callback => requestAnimationFrame(callback),
    private readonly cancel: (handle: number) => void = handle => cancelAnimationFrame(handle),
    private readonly clock: () => number = () => performance.now(),
    private readonly budgetMs = 3,
    private readonly maxPerFrame = 4) {}

  /** Budget deferred work too (e.g. buffers uploaded by the NEXT render).
   * An oversized single task still makes progress; it gets its own frame. */
  withWorkBudget(size: (value: T) => number, limit: number): this {
    this.workBudget = { size, limit };
    return this;
  }

  push(value: T): void {
    if (this.closed) return;
    this.ready.push(value);
    this.request();
  }
  private request(): void {
    if (this.scheduled !== null || this.draining || !this.ready.length || this.closed) return;
    this.scheduled = this.schedule(() => this.drain());
  }
  private async drain(): Promise<void> {
    this.scheduled = null;
    if (this.closed) return;
    this.draining = true;
    const start = this.clock();
    let count = 0, work = 0;
    try {
      do {
        const size = this.workBudget?.size(this.ready[0]) ?? 0;
        if (count && this.workBudget && work + size > this.workBudget.limit) break;
        work += size;
        this.consume(this.ready.shift()!);
        // Resolving a worker promise queues the actual onPatchBuilt work as a
        // microtask. Measure AFTER that work, not just the cheap resolve() call.
        await Promise.resolve();
        count++;
      } while (!this.closed && this.ready.length && count < this.maxPerFrame && this.clock() - start < this.budgetMs);
    } finally { this.draining = false; this.request(); }
  }
  dispose(): void {
    this.closed = true;
    if (this.scheduled !== null) this.cancel(this.scheduled);
    this.scheduled = null;
    this.ready.length = 0;
  }
}
